import React, {
  useEffect,
  useState,
} from "react";

import {
  Box,
  Button,
  Modal,
  Typography,
} from "@mui/material";

import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from "firebase/storage";

import {
  faAdd,
  faEdit,
  faSearch,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  toast,
} from "react-hot-toast";

import {
  tagsApi,
} from "../api/tagsApi";

import {
  ApiError,
} from "../api/apiError";

import type {
  Tag,
} from "../types/tags.types";

import "../screenstyles/Tags.css";
type TagForm = {
  id: string;
  name: string;
  imageUrl: string | null;
  isActive: boolean;
  sortOrder: number;
};

const emptyTagForm: TagForm = {
  id: "",
  name: "",
  imageUrl: null,
  isActive: true,
  sortOrder: 0,
};

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\+/g, "plus")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getTagErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (!(error instanceof ApiError)) {
    return fallbackMessage;
  }

  switch (error.code) {
    case "TAG_NOT_FOUND":
      return "العلامة غير موجودة.";

    case "TAG_ALREADY_INACTIVE":
      return "هذه العلامة غير مفعلة بالفعل.";

    case "TAG_ALREADY_EXISTS":
      return "توجد علامة أخرى بنفس الـ ID.";

    case "TAG_NAME_ALREADY_EXISTS":
      return "توجد علامة أخرى بنفس الاسم.";

    case "FORBIDDEN":
      return "لا تملك الصلاحية المطلوبة لتنفيذ العملية.";

    case "VALIDATION_ERROR":
      return "بيانات العلامة غير صحيحة. تحقق من الحقول.";

    default:
      return error.message || fallbackMessage;
  }
}


const TagsList: React.FC = () => {
  const [tags, setTags] =
    useState<Tag[]>([]);

  const [loading, setLoading] =
    useState(true);

  const [searchQuery, setSearchQuery] =
    useState("");

  const [formOpen, setFormOpen] =
    useState(false);

  const [deleteOpen, setDeleteOpen] =
    useState(false);

  const [editingTagId, setEditingTagId] =
    useState<string | null>(null);

  const [selectedTagId, setSelectedTagId] =
    useState<string | null>(null);

  const [form, setForm] =
    useState<TagForm>(emptyTagForm);

  const [selectedImage, setSelectedImage] =
    useState<File | null>(null);

  const [preview, setPreview] =
    useState<string | null>(null);

  const [saving, setSaving] =
    useState(false);

  const [deleting, setDeleting] =
    useState(false);

  const storage = getStorage();

  const fetchTags = async () => {
    setLoading(true);

    try {
      const result =
        await tagsApi.list({
          sort: "sortOrder_asc",
          limit: 100,
        });

      setTags(result.items);
    } catch (error) {
      console.error(
        "Error fetching tags from API:",
        error,
      );

      toast.error(
        getTagErrorMessage(
          error,
          "حدث خطأ أثناء تحميل العلامات",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const openAddModal = () => {
    setEditingTagId(null);

    setForm({
      ...emptyTagForm,
      sortOrder: tags.length + 1,
    });

    setSelectedImage(null);
    setPreview(null);
    setFormOpen(true);
  };

  const openEditModal = (
    tag: Tag,
  ) => {
    setEditingTagId(tag.id);

    setForm({
      id: tag.id,
      name: tag.name,
      imageUrl: tag.imageUrl,
      isActive: tag.isActive,
      sortOrder: tag.sortOrder,
    });

    setSelectedImage(null);
    setPreview(tag.imageUrl);
    setFormOpen(true);
  };

  const closeFormModal = () => {
    if (saving) return;

    setFormOpen(false);
    setEditingTagId(null);
    setForm(emptyTagForm);
    setSelectedImage(null);
    setPreview(null);
  };

  const handleImageChange = (
    event:
      React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file =
      event.target.files?.[0] ?? null;

    setSelectedImage(file);

    if (!file) {
      setPreview(
        editingTagId
          ? form.imageUrl
          : null,
      );

      return;
    }

    const reader =
      new FileReader();

    reader.onload = () => {
      setPreview(
        reader.result as string,
      );
    };

    reader.readAsDataURL(file);
  };

  const uploadTagImage = async (
    file: File,
  ): Promise<string> => {
    const safeFileName =
      `${Date.now()}-${file.name}`;

    const imageReference =
      ref(
        storage,
        `tagsImage/${safeFileName}`,
      );

    await uploadBytes(
      imageReference,
      file,
    );

    return getDownloadURL(
      imageReference,
    );
  };

  const handleSave = async () => {
    const normalizedName =
      form.name.trim();

    const normalizedSortOrder =
      Number(form.sortOrder);

    const normalizedId =
      editingTagId ||
      slugify(
        form.id ||
        normalizedName,
      );

    if (!normalizedName) {
      toast.error(
        "يرجى إدخال اسم العلامة.",
      );

      return;
    }

    if (
      !editingTagId &&
      !normalizedId
    ) {
      toast.error(
        "يرجى إدخال ID صحيح للعلامة.",
      );

      return;
    }

    if (
      !Number.isInteger(
        normalizedSortOrder,
      ) ||
      normalizedSortOrder < 0
    ) {
      toast.error(
        "الترتيب يجب أن يكون عددًا صحيحًا يساوي صفرًا أو أكثر.",
      );

      return;
    }

    if (
      !editingTagId &&
      !selectedImage
    ) {
      toast.error(
        "يرجى اختيار صورة للعلامة.",
      );

      return;
    }

    try {
      setSaving(true);

      let imageUrl =
        form.imageUrl;

      if (selectedImage) {
        imageUrl =
          await uploadTagImage(
            selectedImage,
          );
      }

      if (editingTagId) {
        await tagsApi.update(
          editingTagId,
          {
            name:
              normalizedName,

            imageUrl:
              imageUrl ?? null,

            isActive:
              Boolean(
                form.isActive,
              ),

            sortOrder:
              normalizedSortOrder,
          },
        );
      } else {
        await tagsApi.create({
          id:
            normalizedId,

          name:
            normalizedName,

          imageUrl:
            imageUrl ?? null,

          isActive:
            Boolean(
              form.isActive,
            ),

          sortOrder:
            normalizedSortOrder,
        });
      }

      toast.success(
        editingTagId
          ? "تم تعديل العلامة بنجاح"
          : "تمت إضافة العلامة بنجاح",
        {
          className:
            "products-toast-success",
        },
      );

      closeFormModal();

      await fetchTags();
    } catch (error) {
      console.error(
        "Error saving tag:",
        error,
      );

      toast.error(
        getTagErrorMessage(
          error,
          editingTagId
            ? "حدث خطأ أثناء تعديل العلامة"
            : "حدث خطأ أثناء إضافة العلامة",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (
    tagId: string,
  ) => {
    setSelectedTagId(tagId);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;

    setSelectedTagId(null);
    setDeleteOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedTagId) return;

    try {
      setDeleting(true);

      const result =
        await tagsApi.delete(
          selectedTagId,
        );

      toast.success(
        result.productsCount > 0
          ? `تم تعطيل العلامة، وهي مرتبطة بـ ${result.productsCount} منتج`
          : "تم تعطيل العلامة بنجاح",
        {
          className:
            "products-toast-success",
        },
      );

      setSelectedTagId(null);
      setDeleteOpen(false);

      await fetchTags();
    } catch (error) {
      console.error(
        "Error disabling tag:",
        error,
      );

      toast.error(
        getTagErrorMessage(
          error,
          "حدث خطأ أثناء تعطيل العلامة",
        ),
      );
    } finally {
      setDeleting(false);
    }
  };

  const normalizedSearch =
    searchQuery
      .trim()
      .toLowerCase();

  const filteredTags =
    normalizedSearch
      ? tags.filter((tag) =>
          [
            tag.id,
            tag.name,
            String(
              tag.sortOrder,
            ),
            tag.isActive
              ? "مفعل"
              : "غير مفعل",
          ]
            .join(" ")
            .toLowerCase()
            .includes(
              normalizedSearch,
            ),
        )
      : tags;

  return (
   <div className="tags-page">
  <div className="tags-header">
    <h1 className="tags-title">
      إدارة العلامات
    </h1>
    <div className="tags-search-box">
          <FontAwesomeIcon
            icon={faSearch}
           className="tags-search-icon"
          />

          <input
            type="text"
            placeholder="ابحث عن العلامات..."
        className="tags-search-input"
            value={searchQuery}
            onChange={(event) =>
              setSearchQuery(
                event.target.value,
              )
            }
          />
        </div>

        <button
          type="button"
     className="tags-add-btn"
          onClick={openAddModal}
        >
          <FontAwesomeIcon
            icon={faAdd}
          />

          <span>
            إضافة علامة جديدة
          </span>
        </button>
      </div>

      {loading ? (
      <div className="tags-loading">
  جاري تحميل البيانات...
</div>
      ) : (
     <div className="tags-table-container">
  <table className="tags-table">
    <thead>
              <tr>
                <th>#</th>
                <th>الصورة</th>
                <th>ID</th>
                <th>اسم العلامة</th>
                <th>الحالة</th>
                <th>الترتيب</th>
                <th>الإجراءات</th>
              </tr>
            </thead>

            <tbody>
              {filteredTags.map(
                (tag, index) => (
                  <tr key={tag.id}>
                    <td>
                      {index + 1}
                    </td>

                    <td>
                     {tag.imageUrl ? (
  <img
    src={tag.imageUrl}
    alt={tag.name}
    className="tags-image"
  />
) : (
  <span className="tags-no-image">
    لا توجد صورة
  </span>
)}
                    </td>

                   <td>
  <span className="tags-id">
    {tag.id}
  </span>
</td>

                  <td className="tags-name">
  {tag.name}
</td>

                    <td>
                      <span
                       className={
  tag.isActive
    ? "tags-status active"
    : "tags-status inactive"
}
                      >
                        {tag.isActive
                          ? "مفعل"
                          : "غير مفعل"}
                      </span>
                    </td>

                    <td>
                      {tag.sortOrder}
                    </td>

                    <td>
                  <div className="tags-actions">
                       <button
  type="button"
  className="
    tags-action-btn
    tags-edit-btn
  "
  onClick={() =>
    openEditModal(tag)
  }
  title="تعديل العلامة"
>
  <FontAwesomeIcon
    icon={faEdit}
  />
</button>

                       <button
  type="button"
  className="
    tags-action-btn
    tags-delete-btn
  "
  onClick={() =>
    openDeleteModal(
      tag.id,
    )
  }
  title="تعطيل العلامة"
  disabled={!tag.isActive}
>
  <FontAwesomeIcon
    icon={faTrash}
  />
</button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>

          {filteredTags.length ===
            0 && (
          <div className="tags-empty-state">
              لا توجد علامات مطابقة.
            </div>
          )}
        </div>
      )}

      {/* مودال الإضافة والتعديل */}
    <Modal
  open={formOpen}
  onClose={closeFormModal}
  className="tags-modal"
>
  <Box className="tags-modal-card">
          <Typography className="tags-modal-title">
  {editingTagId
    ? "تعديل العلامة"
    : "إضافة علامة جديدة"}
</Typography>

         <div className="tags-form-grid">
         <label className="tags-form-field">
              ID
              <input
                type="text"
                value={form.id}
                disabled={
                  Boolean(
                    editingTagId,
                  ) || saving
                }
                onChange={(event) =>
                  setForm({
                    ...form,
                    id: slugify(
                      event.target
                        .value,
                    ),
                  })
                }
                placeholder="video_recorders"
              />
            </label>

           <label className="tags-form-field">
              اسم العلامة
              <input
                type="text"
                value={form.name}
                disabled={saving}
                onChange={(event) =>
                  setForm({
                    ...form,

                    name:
                      event.target
                        .value,

                    id:
                      editingTagId
                        ? form.id
                        : slugify(
                            event
                              .target
                              .value,
                          ),
                  })
                }
                placeholder="Video Recorders"
              />
            </label>

         <label className="tags-form-field">
              الترتيب
              <input
                type="number"
                min="0"
                value={
                  form.sortOrder
                }
                disabled={saving}
                onChange={(event) =>
                  setForm({
                    ...form,
                    sortOrder:
                      Number(
                        event.target
                          .value,
                      ),
                  })
                }
              />
            </label>

           <label className="tags-form-field">
              <input
                type="checkbox"
                checked={
                  form.isActive
                }
                disabled={saving}
                onChange={(event) =>
                  setForm({
                    ...form,
                    isActive:
                      event.target
                        .checked,
                  })
                }
              />

              العلامة مفعلة
            </label>
<div className="tags-upload-box">
  <label className="tags-form-field">
    الصورة

    <input
      type="file"
      accept="image/*"
      disabled={saving}
      onChange={handleImageChange}
      className="tags-file-input"
    />
  </label>

  {preview && (
    <img
      src={preview}
      alt="معاينة العلامة"
      className="tags-preview"
    />
  )}
</div>
          </div>

          <Box
            sx={{
              display: "flex",
              justifyContent:
                "center",
              gap: 2,
              marginTop: 3,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
                className="tags-save-btn"
            >
              {saving
                ? "جاري الحفظ..."
                : editingTagId
                  ? "حفظ التعديلات"
                  : "إضافة العلامة"}
            </Button>

            <Button
              variant="outlined"
                className="tags-cancel-btn"
              onClick={
                closeFormModal
              }
              disabled={saving}
            >
              إلغاء
            </Button>
          </Box>
        </Box>
      </Modal>

   <Modal
  open={deleteOpen}
  onClose={closeDeleteModal}
  className="tags-modal"
>
  <Box
    className="
      tags-modal-card
      tags-delete-modal-card
    "
  >
         <Box className="tags-modal-actions">
  <Button
    className="tags-delete-confirm-btn"
    onClick={handleDelete}
    disabled={deleting}
  >
    {deleting
      ? "جاري التعطيل..."
      : "نعم، عطّل العلامة"}
  </Button>

  <Button
    className="tags-cancel-btn"
    onClick={closeDeleteModal}
    disabled={deleting}
  >
    إلغاء
  </Button>
</Box>

        </Box>
      </Modal>
    </div>
  );
};

export default TagsList;